import { Link } from "react-router-dom";
import { ExternalLink, CalendarCheck, Send } from "lucide-react";
import { siteConfig, isPlaceholder } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";
import { track } from "../lib/tracking";

type Variant = "primary" | "dark" | "outline" | "ghost";
const variantClass: Record<Variant, string> = { primary: "btn-primary", dark: "btn-dark", outline: "btn-outline", ghost: "btn-ghost" };

/**
 * Booking.com-Button. Solange keine echte URL in siteConfig.bookingLinks.bookingCom eingetragen ist,
 * wird der Button deaktiviert angezeigt (kein Fake-Link).
 */
export const BookingButton = ({ variant = "primary", className = "", label, testId = "booking-com-button" }: { variant?: Variant; className?: string; label?: string; testId?: string }) => {
  const { t } = useLanguage();
  const url = siteConfig.bookingLinks.bookingCom;
  const disabled = isPlaceholder(url);
  const text = label ?? t.cta.bookingCom;

  if (disabled) {
    return (
      <span className="inline-flex flex-col items-start gap-1.5">
        <button type="button" disabled aria-disabled="true" className={`${variantClass[variant]} ${className}`} data-testid={testId} title={t.cta.bookingComSoon}>
          <ExternalLink className="h-4 w-4" aria-hidden /> {text}
        </button>
        <span className="text-xs text-charcoal-400" data-testid={`${testId}-hint`}>{t.cta.bookingComSoon}</span>
      </span>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`${variantClass[variant]} ${className}`} data-testid={testId} onClick={() => track("booking_click", { url })}>
      <ExternalLink className="h-4 w-4" aria-hidden /> {text}
    </a>
  );
};

/** Haupt-CTA "Verfügbarkeit prüfen": nutzt Booking.com sobald vorhanden, sonst das Anfrageformular. */
export const AvailabilityButton = ({ variant = "primary", className = "", testId = "check-availability-button" }: { variant?: Variant; className?: string; testId?: string }) => {
  const { t } = useLanguage();
  const url = siteConfig.bookingLinks.bookingCom;
  if (!isPlaceholder(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={`${variantClass[variant]} ${className}`} data-testid={testId} onClick={() => track("booking_click", { url })}>
        <CalendarCheck className="h-4 w-4" aria-hidden /> {t.cta.checkAvailability}
      </a>
    );
  }
  return (
    <Link to="/kontakt#anfrage" className={`${variantClass[variant]} ${className}`} data-testid={testId}>
      <CalendarCheck className="h-4 w-4" aria-hidden /> {t.cta.checkAvailability}
    </Link>
  );
};

export const InquiryButton = ({ variant = "outline", className = "", label, testId = "send-inquiry-button" }: { variant?: Variant; className?: string; label?: string; testId?: string }) => {
  const { t } = useLanguage();
  return (
    <Link to="/kontakt#anfrage" className={`${variantClass[variant]} ${className}`} data-testid={testId}>
      <Send className="h-4 w-4" aria-hidden /> {label ?? t.cta.sendInquiry}
    </Link>
  );
};
